import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'

import type { CustomCellEditorProps } from 'ag-grid-react'
import type { IReview } from '~/types/review'

import MultiSelectInput from '~/components/Inputs/Basic/MultiSelectInput'
import CustomBadgeOption from '~/components/SelectOptions/CustomBadgeOption'
import { useReviewsTopicsQuery } from '~/queries/reviews-topics'

import './styles.scss'

const ReviewTopicCellEditor = forwardRef(
  ({ data, onValueChange, stopEditing }: CustomCellEditorProps<IReview>, ref) => {
    /** Queries & Mutations */

    const { data: topics = [], isLoading: isTopicsLoading } = useReviewsTopicsQuery()

    /** Local state */

    const topicsLabels = data?.topics.map(({ label }) => label) || []

    const [values, setValues] = useState<IOption[]>(
      topics.filter(({ label }) => topicsLabels.includes(label)).map(({ id, label }) => ({ label, value: id }))
    )

    const [editing] = useState(true)

    const refContainer = useRef(null)

    const topicOptions = topics
      .filter(
        ({ applications, is_all_apps }) =>
          is_all_apps ||
          applications?.some(
            ({ app_key, device_type }) => data?.app_key === app_key && data?.device_type === device_type
          )
      )
      .map(({ id, label }) => ({ label, value: id }))

    /** Effects */

    useImperativeHandle(ref, () => {
      return {
        getValue() {
          return values.map((option) => option)
        },
      }
    })

    useEffect(() => {
      if (!editing) {
        stopEditing()
      }
    }, [editing, stopEditing])

    useEffect(() => {
      onValueChange(values)
    }, [onValueChange, values])

    /** Handlers */

    const handleSelectionChange = (newValues: unknown) => setValues(newValues as IOption[])

    return (
      <div
        className="review-topic-cell-editor-component"
        ref={refContainer}
        tabIndex={1} // important - without this the key presses wont be caught
      >
        <MultiSelectInput
          className="editable-topic-input"
          components={{ Option: (props) => <CustomBadgeOption {...props} outline /> }}
          isLoading={isTopicsLoading}
          menuClassName="editable-topic-input__menu"
          onChange={handleSelectionChange}
          options={topicOptions}
          value={values}
        />
      </div>
    )
  }
)

ReviewTopicCellEditor.displayName = 'ReviewTopicCellEditor'

export default ReviewTopicCellEditor
